<script setup lang="ts">
import type { SearchResultAssetFragment } from '#graphql-operations'
import type { CarouselApi } from '@layers/ui/app/components/ui/carousel'

const props = defineProps<{
  images: (SearchResultAssetFragment & { alt: string })[]
  imageIndex: number
}>()

const show = defineModel('show', { default: false })

const emblaMainApi = ref<CarouselApi>()
const emblaThumbnailApi = ref<CarouselApi>()
const selectedIndex = ref(props.imageIndex)

const emblaThumbnailRef = ref<HTMLElement | null>(null)
const emblaMainRef = ref<HTMLElement | null>(null)
const clickedOutsideMain = ref(false)
const clickedOutsideThumbnail = ref(false)

function onSelect() {
  if (!emblaMainApi.value || !emblaThumbnailApi.value)
    return
  selectedIndex.value = emblaMainApi.value.selectedScrollSnap()
  emblaThumbnailApi.value.scrollTo(emblaMainApi.value.selectedScrollSnap())
}

function scrollTo(index: number, jump = false) {
  if (!emblaMainApi.value || !emblaThumbnailApi.value)
    return
  emblaMainApi.value.scrollTo(index, jump)
}

watchOnce(emblaMainApi, (emblaMainApi) => {
  if (!emblaMainApi)
    return

  onSelect()
  emblaMainApi.on('select', onSelect)
  emblaMainApi.on('reInit', onSelect)
})

onClickOutside(emblaMainRef, () => {
  clickedOutsideMain.value = true
  show.value = false
}, {
  ignore: [emblaThumbnailRef],
})

onClickOutside(emblaThumbnailRef, () => {
  clickedOutsideThumbnail.value = true
  show.value = false
}, {
  ignore: [emblaMainRef],
})
</script>

<template>
  <HTransitionRoot appear :show="show" as="template">
    <HDialog as="div" class="relative z50" @close="show = false">
      <HTransitionChild as="template" enter="duration-300 ease-out" enter-from="op0" enter-to="op100" leave="duration-200 ease-in" leave-from="op100" leave-to="op0">
        <div class="fixed inset-0 bg-black sm:bg-black/75" />
      </HTransitionChild>

      <div class="fixed inset-0 w-screen of-y-auto">
        <div class="relative min-h-full flex items-center justify-center sm:p8">
          <div class="absolute right-6 top-6">
            <NButton
              id="close-sheet"
              n="xl borderless"
              icon="i-heroicons:x-mark"
              class="p-0.25em c-white children:m-0 hover:c-slate-100"
              @click="show = false"
            />
          </div>

          <HTransitionChild as="template" enter="duration-300 ease-out" enter-from="op0 scale-95" enter-to="op100 scale-100" leave="duration-200 ease-in" leave-from="op100 scale-100" leave-to="op0 scale-95">
            <HDialogPanel
              id="product-gallery-lightbox"
              class="relative h-full w-full"
            >
              <div class="flex py8">
                <div class="mr8 hidden sm:block">
                  <UiCarousel
                    ref="emblaThumbnailRef"
                    orientation="vertical"
                    :opts="{
                      align: 'start',
                      startIndex: selectedIndex,
                    }"
                    @init-api="(val) => emblaThumbnailApi = val"
                  >
                    <UiCarouselContent class="ml0 hfull max-h-xl flex gap-2">
                      <UiCarouselItem
                        v-for="(image, index) in images" :key="index"
                        class="basis-[calc((100%-(5*0.5rem))/6)] cursor-pointer pl0" @click="scrollTo(index)"
                      >
                        <div
                          class="border-2 n-border-base"
                          :class="index === selectedIndex ? 'border-primary-950' : 'op50'"
                        >
                          <NuxtImg
                            :src="image.preview"
                            :alt="image.alt"
                            width="150"
                            height="150"
                            placeholder
                            class="aspect-square hfull wfull select-none select-none object-cover object-center"
                            loading="lazy"
                          />
                        </div>
                      </UiCarouselItem>
                    </UiCarouselContent>
                  </UiCarousel>
                </div>

                <div class="hauto wfull flex items-center justify-center sm:px12">
                  <UiCarousel
                    ref="emblaMainRef"
                    class="relative mb4 max-w-4xl wfull"
                    :opts="{ duration: 20, startIndex: selectedIndex }"
                    @init-api="(val) => emblaMainApi = val"
                  >
                    <UiCarouselContent>
                      <UiCarouselItem v-for="(image, index) in images" :key="index">
                        <NuxtImg
                          :src="image.preview"
                          :alt="image.alt"
                          width="640"
                          height="640"
                          class="aspect-square hfull wfull select-none object-cover object-center"
                          preload
                        />
                      </UiCarouselItem>
                    </UiCarouselContent>
                    <UiCarouselPrevious class="absolute top-1/2 bg-background hidden -left-16 !h12 !w12 sm:flex hover:n-button-hover">
                      <Icon name="i-heroicons:chevron-left" class="text-xl" />
                    </UiCarouselPrevious>
                    <UiCarouselNext class="absolute top-1/2 bg-background hidden -right-16 !h12 !w12 sm:flex hover:n-button-hover">
                      <Icon name="i-heroicons:chevron-right" class="text-xl" />
                    </UiCarouselNext>

                    <div class="absolute bottom-4 right-4 z10 rounded-full bg-slate-900/50 sm:hidden">
                      <span class="px2 text-context fw600 leading-6" n="sm white">
                        {{ selectedIndex + 1 }} / {{ images?.length ?? 0 }}
                      </span>
                    </div>

                    <slot />
                  </UiCarousel>
                </div>
              </div>
            </HDialogPanel>
          </HTransitionChild>
        </div>
      </div>
    </HDialog>
  </HTransitionRoot>
</template>
